import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  Autocomplete,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Badge,
  Link,
  Container,
  Collapse,
  DialogContentText,
  OutlinedInput,
  Card,
  CardContent,
  alpha,
  useTheme,
  List,
  ListItem,
  ListItemText,
  DialogContent as MuiDialogContent,
  styled,
  InputBase,
  Paper as MuiPaper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  FilterList as FilterListIcon,
  LinkOff as UnlinkedIcon,
  LibraryAdd as NewEntryIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as GetAppIcon,
  TrendingUp,
  TrendingDown,
  Search as SearchIcon,
  MonetizationOn as MoneyIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  AccountBalance as BankIcon,
  Refresh,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

import * as indexedDBService from '../utils/indexedDBService';
import { InvoiceService } from '../services/invoiceService';
import { formatDate, formatCurrency } from '../utils/formatters';
import { CashTransaction, TRANSACTION_CATEGORIES } from '../types/accounting';
import { motion } from 'framer-motion';
import AnimatedPage from '../components/AnimatedPage';

interface NewTransaction extends Omit<CashTransaction, 'id' | 'timestamp' | 'balance'> {
  linkedEntityType?: 'client' | 'vendor' | 'expense' | 'advance' | 'loan' | 'capital' | 'purchase' | '';
  linkedEntityId?: string;
  linkedEntityName?: string;
  linkedInvoiceId?: string;
  createdAt: number;
}

interface Client {
  id: string;
  name: string;
  displayName?: string;
}

interface Vendor {
  id: string;
  name: string;
  displayName?: string;
}

interface Expense {
  id: string;
  name: string;
  category: string;
  description?: string;
  displayName?: string;
}

interface EntityOption {
  id: string;
  name: string;
}

// Add interface for invoices
interface Invoice {
  id: string;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  totalAmount: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  clientId?: string;
}

interface Purchase {
  id: string;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  totalAmount: number;
  status: 'draft' | 'pending' | 'paid';
  vendorId?: string;
  userId: string;
}

// Add styled components for the search dialog
const CustomDialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: '100%',
    fontSize: '1.1rem',
  },
}));

const CashBook: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();
  const [transactions, setTransactions] = useState<CashTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(
    userProfile?.accountSettings?.financialYearStart
      ? new Date(userProfile.accountSettings.financialYearStart)
      : new Date(new Date().getFullYear(), 3, 1) // Default to April 1st
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [showFilters, setShowFilters] = useState(false);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [showUnlinkedOnly, setShowUnlinkedOnly] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // State for opening balance
  const [openingBalance, setOpeningBalance] = useState<number>(userProfile?.accountSettings?.openingCashBalance || 0);
  const [openingBalanceDate, setOpeningBalanceDate] = useState<Date | null>(
    userProfile?.accountSettings?.financialYearStart
      ? new Date(userProfile.accountSettings.financialYearStart)
      : new Date(new Date().getFullYear(), 3, 1) // Default to April 1st
  );
  const [showOpeningBalanceDialog, setShowOpeningBalanceDialog] = useState<boolean>(false);

  // New transaction state with linking information
  const [newTransaction, setNewTransaction] = useState<NewTransaction>({
    userId: currentUser?.uid || '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: 0,
    type: 'debit',
    category: 'General',
    reference: '',
    linkedEntityType: undefined,
    linkedEntityId: undefined,
    linkedEntityName: undefined,
    linkedInvoiceId: undefined,
    createdAt: Date.now()
  });

  // Reference data for linking
  const [clients, setClients] = useState<Client[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [createNewExpense, setCreateNewExpense] = useState(false);
  const [newExpense, setNewExpense] = useState<Omit<Expense, 'id'>>({
    name: '',
    category: 'General',
    description: ''
  });

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [transactionToDelete, setTransactionToDelete] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);

  // Entity options state
  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([]);

  // Additional state for invoices
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [linkedInvoices, setLinkedInvoices] = useState<(Invoice | Purchase)[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<string>('');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  // Add search state variables
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showSearchDialog, setShowSearchDialog] = useState<boolean>(false);

  useEffect(() => {
    if (currentUser) {
      fetchTransactions();
      fetchReferenceData();
      fetchInvoicesAndPurchases();
    }
  }, [currentUser, startDate, endDate]);

  // Fetch clients, vendors, and expenses
  const fetchReferenceData = async () => {
    if (!currentUser) return;

    try {
      // Fetch clients
      const clientsData = await indexedDBService.getAllItems<any>('clients');
      const mappedClients = clientsData.map(c => ({
        id: c.id,
        ...c,
        displayName: c.name || c.businessName
      }));
      setClients(mappedClients);

      // Fetch vendors
      const vendorsData = await indexedDBService.getAllItems<any>('vendors');
      const mappedVendors = vendorsData.map(v => ({
        id: v.id,
        ...v,
        displayName: v.name || v.businessName
      }));
      setVendors(mappedVendors);

      // Fetch expenses
      const expensesData = await indexedDBService.getAllItems<any>('expenses');
      const mappedExpenses = expensesData.map(e => ({
        id: e.id,
        ...e,
        displayName: e.name || e.description
      }));
      setExpenses(mappedExpenses);
    } catch (error) {
      console.error('Error fetching reference data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading clients, vendors, and expenses',
        severity: 'error'
      });
    }
  };

  // Fetch transactions from IndexedDB
  const fetchTransactions = async () => {
    if (!currentUser) return;
    setLoading(true);

    try {
      const allTransactions = await indexedDBService.getAllItems<CashTransaction>('cashTransactions');
      // Sort ASC by createdAt initially for calculation
      const sortedTransactions = allTransactions
        .filter(t => t.userId === currentUser.uid)
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

      // Calculate running balance
      let runningBalance = openingBalance;
      const transactionsWithBalance = sortedTransactions.map(transaction => {
        if (transaction.type === 'credit') {
          runningBalance += transaction.amount ? parseFloat(String(transaction.amount)) : 0;
        } else {
          runningBalance -= transaction.amount ? parseFloat(String(transaction.amount)) : 0;
        }
        return { ...transaction, balance: runningBalance };
      });

      // Filter by date and Sort DESC for display
      let displayList = transactionsWithBalance;

      if (startDate) {
        const startDateStr = startDate.toISOString().split('T')[0];
        displayList = displayList.filter(t => t.date >= startDateStr);
      }

      if (endDate) {
        const endDateStr = endDate.toISOString().split('T')[0];
        displayList = displayList.filter(t => t.date <= endDateStr);
      }

      // Sort newest first for display
      displayList.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      setTransactions(displayList);

    } catch (error) {
      console.error('Error fetching cash transactions:', error);
      setSnackbar({
        open: true,
        message: 'Error loading cash transactions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoices and purchases
  const fetchInvoicesAndPurchases = async () => {
    if (!currentUser) return;

    try {
      // Fetch client invoices
      const invoicesData = await InvoiceService.getInvoices(currentUser.uid);
      const pendingInvoices = invoicesData.filter(inv =>
        inv.status === 'pending' || inv.status === 'overdue'
      ) as unknown as Invoice[];
      setInvoices(pendingInvoices);

      // Fetch vendor purchases
      const purchasesData = await indexedDBService.getAllItems<Purchase>('purchases');
      const pendingPurchases = purchasesData.filter(p => p.userId === currentUser.uid && p.status === 'pending');
      setPurchases(pendingPurchases);

    } catch (error) {
      console.error('Error fetching invoices and purchases:', error);
      setSnackbar({
        open: true,
        message: 'Error loading invoices and purchases',
        severity: 'error'
      });
    }
  };

  // Handle selection changes for linking
  const handleLinkSelectionChange = (type: 'client' | 'vendor' | 'expense', entity: any | null) => {
    // Clear previous selections
    setSelectedClient(type === 'client' ? entity : null);
    setSelectedVendor(type === 'vendor' ? entity : null);
    setSelectedExpense(type === 'expense' ? entity : null);

    // Update transaction with linked entity
    if (entity) {
      setNewTransaction({
        ...newTransaction,
        linkedEntityType: type,
        linkedEntityId: entity.id,
        linkedEntityName: entity.displayName || entity.name
      });
    } else {
      setNewTransaction({
        ...newTransaction,
        linkedEntityType: undefined,
        linkedEntityId: undefined,
        linkedEntityName: undefined
      });
    }
  };

  // Create a new expense for linking
  const handleCreateNewExpense = async () => {
    if (!currentUser || !newExpense.name || !newExpense.category) {
      setSnackbar({
        open: true,
        message: 'Please provide expense name and category',
        severity: 'error'
      });
      return;
    }

    try {
      const expenseData = {
        userId: currentUser.uid,
        name: newExpense.name,
        category: newExpense.category,
        description: newExpense.description,
        createdAt: Date.now()
      };

      const id = await indexedDBService.addItem('expenses', expenseData);

      const newExpenseEntry = {
        id: id as string,
        ...expenseData,
        displayName: expenseData.name
      };

      // Add to expenses state and select it
      setExpenses(prev => [...prev, newExpenseEntry]);
      setSelectedExpense(newExpenseEntry);

      // Update transaction with linked expense
      setNewTransaction({
        ...newTransaction,
        linkedEntityType: 'expense',
        linkedEntityId: id as string,
        linkedEntityName: newExpense.name
      });

      // Reset new expense form and close it
      setNewExpense({
        name: '',
        category: '',
        description: ''
      });
      setCreateNewExpense(false);

      setSnackbar({
        open: true,
        message: 'New expense created and linked to transaction',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating expense:', error);
      setSnackbar({
        open: true,
        message: 'Error creating expense',
        severity: 'error'
      });
    }
  };

  // Handle input changes for new expense
  const handleNewExpenseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewExpense(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Filter unlinked transactions toggle
  const handleUnlinkedToggle = () => {
    setShowUnlinkedOnly(!showUnlinkedOnly);
  };

  // Add new transaction with linking
  const handleAddTransaction = async () => {
    if (!currentUser) return;

    if (!newTransaction.description || !newTransaction.category || newTransaction.amount <= 0) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    // Validate linking based on transaction type (Bypassed to allow effortless logging of general manual expenses/entries)
    /*
    if (newTransaction.type === 'debit' && !newTransaction.linkedEntityType) {
      setSnackbar({
        open: true,
        message: 'Please link this debit transaction to a vendor or expense',
        severity: 'error'
      });
      return;
    }

    if (newTransaction.type === 'credit' && !newTransaction.linkedEntityType) {
      setSnackbar({
        open: true,
        message: 'Please link this credit transaction to a client',
        severity: 'error'
      });
      return;
    }
    */

    try {
      // Get the last balance for running balance calculation
      const lastTransaction = transactions.length > 0
        ? transactions[0] // Since we sort DESC for display, the first item is the NEWEST (last transaction) ???
        // Wait, if sort is DESC (newest first), then transactions[0] IS the newest. Correct.
        // But if I add a NEW transaction at "Now", it becomes the newest.
        // So its balance = (Balance of Previous Newest) + Amount.
        // So relying on `transactions[0].balance` is correct for running balance IF we assume we are adding to the END of time.
        : null;

      let newBalance = lastTransaction
        ? lastTransaction.balance
        : openingBalance;

      // Update running balance based on transaction type
      if (newTransaction.type === 'credit') {
        newBalance += newTransaction.amount ? parseFloat(String(newTransaction.amount)) : 0;
      } else {
        newBalance -= newTransaction.amount ? parseFloat(String(newTransaction.amount)) : 0;
      }

      // Prepare transaction data
      const transactionData: Omit<CashTransaction, 'id'> = {
        ...newTransaction,
        userId: currentUser.uid,
        balance: newBalance,
        createdAt: Date.now()
      };

      // Add the cash transaction
      const id = await indexedDBService.addItem('cashTransactions', transactionData);

      // Handle invoice linking and status updates
      if (newTransaction.linkedInvoiceId) {
        if (newTransaction.linkedEntityType === 'client') {
          // Update invoice status to paid
          const invoice = await indexedDBService.getItemById<Invoice>('invoices', newTransaction.linkedInvoiceId);
          if (invoice) {
            await indexedDBService.putItem('invoices', {
              ...invoice,
              status: 'paid',
              paymentDate: new Date().toISOString().split('T')[0]
            });
          }

          // Update client's outstanding balance if needed
          if (newTransaction.linkedEntityId) {
            try {
              const client = await indexedDBService.getItemById<Client>('clients', newTransaction.linkedEntityId);
              if (client) {
                const currentPending = (client as any).pendingAmount || 0;
                const amount = newTransaction.amount ? parseFloat(String(newTransaction.amount)) : 0;
                await indexedDBService.putItem('clients', {
                  ...client,
                  pendingAmount: (currentPending || 0) - amount
                });
              }
            } catch (clientError) {
              console.error('Error updating client pending amount:', clientError);
            }
          }
        } else if (newTransaction.linkedEntityType === 'vendor') {
          // Update purchase status to paid
          const purchase = await indexedDBService.getItemById<Purchase>('purchases', newTransaction.linkedInvoiceId);
          if (purchase) {
            await indexedDBService.putItem('purchases', {
              ...purchase,
              status: 'paid',
              paymentDate: new Date().toISOString().split('T')[0]
            });
          }

          // Update vendor's outstanding balance if needed
          if (newTransaction.linkedEntityId) {
            try {
              const vendor = await indexedDBService.getItemById<Vendor>('vendors', newTransaction.linkedEntityId);
              if (vendor) {
                const currentPending = (vendor as any).pendingAmount || 0;
                const amount = newTransaction.amount ? parseFloat(String(newTransaction.amount)) : 0;
                await indexedDBService.putItem('vendors', {
                  ...vendor,
                  pendingAmount: (currentPending || 0) - amount
                });
              }
            } catch (vendorError) {
              console.error('Error updating vendor pending amount:', vendorError);
            }
          }
        }
      }

      // If this is an expense entry with no linked expense, create one
      if (newTransaction.linkedEntityType === 'expense' &&
        newTransaction.linkedEntityId === 'manual-entry' &&
        newTransaction.linkedEntityName) {
        try {
          // Create a new expense record
          const expenseData = {
            userId: currentUser.uid,
            name: newTransaction.description,
            category: newTransaction.category,
            description: newTransaction.linkedEntityName,
            amount: newTransaction.amount ? parseFloat(String(newTransaction.amount)) : 0,
            date: newTransaction.date,
            createdAt: Date.now()
          };

          await indexedDBService.addItem('expenses', expenseData);
        } catch (expenseError) {
          console.error('Error creating expense record:', expenseError);
        }
      }

      setSnackbar({
        open: true,
        message: 'Transaction added successfully',
        severity: 'success'
      });

      // Reset form and close dialog
      setNewTransaction({
        userId: currentUser?.uid || '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        amount: 0,
        type: 'debit',
        category: '',
        reference: '',
        linkedEntityType: '',
        linkedEntityId: '',
        linkedEntityName: '',
        linkedInvoiceId: '',
        createdAt: Date.now()
      });
      setSelectedClient(null);
      setSelectedVendor(null);
      setSelectedExpense(null);
      setOpenAddDialog(false);

      // Refresh transactions
      fetchTransactions();
    } catch (error) {
      console.error('Error adding transaction:', error);
      setSnackbar({
        open: true,
        message: 'Error adding transaction',
        severity: 'error'
      });
    }
  };

  // Handle input changes for new transaction
  const handleInputChange = (name: string, value: any) => {
    // Special handling for type field - update related fields
    if (name === 'type') {
      // Reset linked entity when changing transaction type
      setNewTransaction(prev => ({
        ...prev,
        [name]: value,
        linkedEntityType: '',
        linkedEntityId: '',
        linkedEntityName: '',
        linkedInvoiceId: ''
      }));

      // Reset related states
      setEntityOptions([]);
      setLinkedInvoices([]);
      setSelectedInvoice('');
    } else {
      setNewTransaction(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Filter transactions
  const filterTransactions = () => {
    return transactions.filter(transaction => {
      // Date filter
      // Date filter
      const d = transaction.date;
      const transactionDate = (d && typeof d === 'object' && d !== null && 'toDate' in (d as any))
        ? new Date((d as any).toDate())
        : new Date(d || Date.now());

      if (startDate && transactionDate < startDate) return false;
      if (endDate) {
        // Set end date to end of day
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (transactionDate > endOfDay) return false;
      }

      // Transaction type filter
      if (transactionTypeFilter.length > 0 && !transactionTypeFilter.includes(transaction.type)) {
        return false;
      }

      // Category filter
      if (categoryFilter.length > 0 && !categoryFilter.includes(transaction.category)) {
        return false;
      }

      // Unlinked filter
      if (showUnlinkedOnly && transaction.linkedEntityId) {
        return false;
      }

      // Search term filter
      if (searchTerm) {
        const lowercaseSearch = searchTerm.toLowerCase();
        const descriptionMatch = transaction.description?.toLowerCase().includes(lowercaseSearch);
        const categoryMatch = transaction.category?.toLowerCase().includes(lowercaseSearch);
        const referenceMatch = transaction.reference?.toLowerCase().includes(lowercaseSearch);
        const amountMatch = transaction.amount.toString().includes(lowercaseSearch);
        const linkedEntityMatch = transaction.linkedEntityName?.toLowerCase().includes(lowercaseSearch);

        if (!(descriptionMatch || categoryMatch || referenceMatch || amountMatch || linkedEntityMatch)) {
          return false;
        }
      }

      return true;
    });
  };

  // Apply filters and get filtered transactions
  const filteredTransactions = filterTransactions();

  // Handle filter changes
  const handleTransactionTypeFilterChange = (event: SelectChangeEvent<typeof transactionTypeFilter>) => {
    const value = event.target.value;
    setTransactionTypeFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleCategoryFilterChange = (event: SelectChangeEvent<typeof categoryFilter>) => {
    const value = event.target.value;
    setCategoryFilter(typeof value === 'string' ? value.split(',') : value);
  };

  // Toggle filters visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Export transactions to CSV
  const handleExportToCSV = () => {
    try {
      // Filter transactions according to current filters
      const filteredData = filterTransactions();

      // Create CSV headers
      const headers = [
        'Date',
        'Description',
        'Reference',
        'Category',
        'Type',
        'Amount',
        'Balance',
        'Linked To'
      ].join(',');

      // Create opening balance row
      const openingBalanceRow = [
        openingBalanceDate ? formatDate(openingBalanceDate.toISOString().split('T')[0]) : startDate ? formatDate(startDate.toISOString().split('T')[0]) : 'N/A',
        'Opening Balance',
        '',
        '',
        '',
        openingBalance,
        openingBalance,
        ''
      ].join(',');

      // Create CSV rows
      const rows = filteredData.map(transaction => {
        const linkedInfo = transaction.linkedEntityName
          ? `${transaction.linkedEntityName} (${transaction.linkedEntityType})`
          : '';

        return [
          formatDate(transaction.date),
          `"${transaction.description}"`,
          transaction.reference || '',
          transaction.category,
          transaction.type,
          transaction.amount,
          transaction.balance,
          linkedInfo
        ].join(',');
      });

      // Combine headers and rows
      const csv = [headers, openingBalanceRow, ...rows].join('\n');

      // Create and download file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `cash-book-export-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: 'Transactions exported successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting transactions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export transactions',
        severity: 'error'
      });
    }
  };

  // Handle date changes
  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
  };

  // Handle type change
  const handleTypeChange = (e: SelectChangeEvent) => {
    // Reset entity selections when switching types
    setSelectedClient(null);
    setSelectedVendor(null);
    setSelectedExpense(null);

    setNewTransaction({
      ...newTransaction,
      type: e.target.value as 'debit' | 'credit',
      linkedEntityType: undefined,
      linkedEntityId: undefined,
      linkedEntityName: undefined
    });
  };

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Fetch entity options when linked entity type changes
  const fetchEntityOptions = useCallback(async (entityType: 'client' | 'vendor' | 'expense' | 'advance' | 'loan' | 'capital' | 'purchase' | '') => {
    if (!currentUser || !entityType) {
      setEntityOptions([]);
      return;
    }

    try {
      let collectionName = entityType + 's'; // clients, vendors, expenses

      const allItems = await indexedDBService.getAllItems<any>(collectionName);
      const userItems = allItems.filter(item => item.userId === currentUser.uid);

      const options: EntityOption[] = userItems.map(doc => ({
        id: doc.id,
        name: doc.name || doc.businessName || doc.description || 'Unnamed',
      }));

      setEntityOptions(options);
    } catch (error) {
      console.error(`Error fetching ${entityType} options:`, error);
      setSnackbar({
        open: true,
        message: `Failed to load ${entityType} options`,
        severity: 'error',
      });
    }
  }, [currentUser]);

  // Handle entity type change
  const handleEntityTypeChange = (entityType: 'client' | 'vendor' | 'expense' | 'advance' | 'loan' | 'capital' | 'purchase' | '') => {
    // Always ensure entityType is treated as a string, even if it's undefined
    const safeEntityType = entityType || '';

    setNewTransaction({
      ...newTransaction,
      linkedEntityType: safeEntityType,
      linkedEntityId: '',
      linkedEntityName: '',
      linkedInvoiceId: ''
    });

    // Reset selected invoice
    setSelectedInvoice('');

    if (safeEntityType === 'client') {
      fetchEntityOptions(safeEntityType);
      // Also get pending invoices for this client type
      setLinkedInvoices(invoices);
    } else if (safeEntityType === 'vendor') {
      fetchEntityOptions(safeEntityType);
      // Get pending purchases
      setLinkedInvoices(purchases);
    } else if (safeEntityType === 'expense') {
      fetchEntityOptions(safeEntityType);
      setLinkedInvoices([]);
    } else {
      // For types that don't need linked entities from database
      setEntityOptions([]);
      setLinkedInvoices([]);
    }
  };

  // Handle entity selection
  const handleEntityChange = (entityId: string) => {
    const selectedEntity = entityOptions.find(entity => entity.id === entityId);

    setNewTransaction({
      ...newTransaction,
      linkedEntityId: entityId,
      linkedEntityName: selectedEntity ? selectedEntity.name : ''
    });

    // Filter invoices based on selected entity if it's a client or vendor
    if (newTransaction.linkedEntityType === 'client') {
      const filteredInvoices = invoices.filter(invoice => invoice.clientId === entityId);
      setLinkedInvoices(filteredInvoices);
    } else if (newTransaction.linkedEntityType === 'vendor') {
      const filteredPurchases = purchases.filter(purchase => purchase.vendorId === entityId);
      setLinkedInvoices(filteredPurchases);
    }
  };

  // Handle invoice selection
  const handleInvoiceSelection = (invoiceId: string) => {
    setSelectedInvoice(invoiceId);

    // Find the selected invoice or purchase
    const selectedDoc = linkedInvoices.find(item => item.id === invoiceId);

    if (selectedDoc) {
      // Update transaction amount to match invoice amount
      setNewTransaction({
        ...newTransaction,
        linkedInvoiceId: invoiceId,
        amount: selectedDoc.totalAmount
      });
    }
  };

  // Validation
  const isFormValid = (): boolean => {
    return (
      newTransaction.date.trim() !== '' &&
      newTransaction.description.trim() !== '' &&
      newTransaction.amount > 0 &&
      (newTransaction.type === 'debit' || newTransaction.type === 'credit') &&
      newTransaction.category !== ''
    );
  };

  // Dialog handlers
  const handleOpenAddDialog = () => {
    setEditMode(false);
    setNewTransaction({
      userId: currentUser?.uid || '',
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: 0,
      type: 'debit',
      category: '',
      reference: '',
      linkedEntityType: '',
      linkedEntityId: '',
      linkedEntityName: '',
      linkedInvoiceId: '',
      createdAt: Date.now()
    });
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setEditMode(false);
  };

  // Edit transaction
  const handleEditTransaction = (transaction: CashTransaction) => {
    setEditMode(true);
    setNewTransaction({
      userId: currentUser?.uid || '',
      date: formatDate(transaction.date),
      description: transaction.description,
      amount: transaction.amount,
      type: transaction.type,
      category: transaction.category,
      reference: transaction.reference || '',
      linkedEntityType: transaction.linkedEntityType || '',
      linkedEntityId: transaction.linkedEntityId || '',
      linkedEntityName: transaction.linkedEntityName || '',
      linkedInvoiceId: transaction.linkedInvoiceId || '',
      createdAt: transaction.createdAt || Date.now()
    });

    if (transaction.linkedEntityType) {
      fetchEntityOptions(transaction.linkedEntityType);
    }

    setOpenAddDialog(true);
  };

  // Delete transaction
  const handleDeleteTransaction = (id: string) => {
    setTransactionToDelete(id);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteTransaction = async () => {
    if (!transactionToDelete || !currentUser) return;

    try {
      await indexedDBService.deleteItem('cashTransactions', transactionToDelete);

      setSnackbar({
        open: true,
        message: 'Transaction deleted successfully',
        severity: 'success',
      });

      // Refresh transactions
      fetchTransactions();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete transaction',
        severity: 'error',
      });
    } finally {
      setOpenDeleteDialog(false);
      setTransactionToDelete(null);
    }
  };

  // Handle opening balance dialog
  const handleOpenOpeningBalanceDialog = () => {
    setShowOpeningBalanceDialog(true);
  };

  const handleCloseOpeningBalanceDialog = () => {
    setShowOpeningBalanceDialog(false);
  };

  const handleSaveOpeningBalance = async () => {
    if (!currentUser) return;

    try {
      // Update user profile in IndexedDB
      const user = await indexedDBService.getItemById<any>('user_profiles', currentUser.uid);
      if (user) {
        await indexedDBService.putItem('user_profiles', {
          ...user,
          accountSettings: {
            ...(user.accountSettings || {}),
            openingCashBalance: openingBalance,
            financialYearStart: openingBalanceDate?.toISOString().split('T')[0]
          }
        });
      }

      setSnackbar({
        open: true,
        message: 'Opening balance saved successfully',
        severity: 'success'
      });

      // Refresh transactions to recalculate balances
      fetchTransactions();
      setShowOpeningBalanceDialog(false);
    } catch (error) {
      console.error('Error saving opening balance:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save opening balance',
        severity: 'error'
      });
    }
  };

  // Add handleSearchChange function
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Add function to toggle search dialog
  const toggleSearchDialog = () => {
    setShowSearchDialog(!showSearchDialog);
    if (!showSearchDialog) {
      setSearchTerm('');
    }
  };

  // Add clearFilters function
  const clearFilters = () => {
    // Keep dates as they are often needed for valid data display
    // but reset all other filters
    setTransactionTypeFilter([]);
    setCategoryFilter([]);
    setShowUnlinkedOnly(false);
    setSearchTerm('');

    // Show a confirmation message
    setSnackbar({
      open: true,
      message: 'Filters have been cleared',
      severity: 'success'
    });
  };

  return (
    <AnimatedPage>
      <Box sx={{ p: 3 }}>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{
            background: 'linear-gradient(45deg, #00796B 30%, #26A69A 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Cash Book
          </Typography>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            px: 1,
            maxWidth: 300,
            flexGrow: 1,
            mx: 2
          }}>
            <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
            <InputBase
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ flex: 1 }}
              size="small"
            />
          </Box>

          <Box display="flex" gap={2}>
            <Tooltip title="Set Opening Balance">
              <Button
                variant="outlined"
                onClick={handleOpenOpeningBalanceDialog}
                startIcon={<MoneyIcon />}
                size="small"
                sx={{ borderColor: '#00796B', color: '#00796B' }}
              >
                Opening Balance
              </Button>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{
                background: 'linear-gradient(45deg, #00796B 30%, #26A69A 90%)',
                color: 'white',
                fontWeight: 'medium',
              }}
            >
              Add Transaction
            </Button>
          </Box>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6} lg={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(0, 121, 107, 0.15) 0%, rgba(38, 166, 154, 0.05) 100%)',
                border: '1px solid rgba(0, 121, 107, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(0, 121, 107, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 121, 107, 0.2)',
                    color: '#00796B',
                    mr: 2
                  }}
                >
                  <MoneyIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Opening Balance
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#00796B' }}>
                {formatCurrency(openingBalance)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.15) 0%, rgba(92, 107, 192, 0.05) 100%)',
                border: '1px solid rgba(63, 81, 181, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(63, 81, 181, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(63, 81, 181, 0.2)',
                    color: '#3F51B5',
                    mr: 2
                  }}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Total Credits
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#3F51B5' }}>
                {formatCurrency(filterTransactions().reduce((total, t) => t.type === 'credit' ? total + t.amount : total, 0))}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.15) 0%, rgba(255, 112, 67, 0.05) 100%)',
                border: '1px solid rgba(255, 87, 34, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(255, 87, 34, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 87, 34, 0.2)',
                    color: '#FF5722',
                    mr: 2
                  }}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Total Debits
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#FF5722' }}>
                {formatCurrency(filterTransactions().reduce((total, t) => t.type === 'debit' ? total + t.amount : total, 0))}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(0, 150, 136, 0.15) 0%, rgba(77, 182, 172, 0.05) 100%)',
                border: '1px solid rgba(0, 150, 136, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(0, 150, 136, 0.15)',
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 150, 136, 0.2)',
                    color: '#009688',
                    mr: 2
                  }}
                >
                  <BankIcon fontSize="small" />
                </Box>
                <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                  Current Balance
                </Typography>
              </Box>

              <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, color: '#004D40' }}>
                {formatCurrency(
                  openingBalance +
                  filterTransactions().reduce((total, t) => (
                    t.type === 'credit' ? total + t.amount : total - t.amount
                  ), 0)
                )}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filter Controls */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={2} alignItems="center">
            <Box>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>
            <Box>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={toggleFilters}
            >
              Filters
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={showUnlinkedOnly}
                  onChange={handleUnlinkedToggle}
                  size="small"
                />
              }
              label="Unlinked Only"
            />
          </Box>

          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportToCSV}
              size="small"
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchTransactions}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Expanded Filters */}
        <Collapse in={showFilters} timeout="auto" unmountOnExit>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="medium">
                Advanced Filters
              </Typography>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Transaction Type</InputLabel>
                  <Select
                    multiple
                    value={transactionTypeFilter}
                    onChange={handleTransactionTypeFilterChange}
                    label="Transaction Type"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="credit">Credit</MenuItem>
                    <MenuItem value="debit">Debit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    multiple
                    value={categoryFilter}
                    onChange={handleCategoryFilterChange}
                    label="Category"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {TRANSACTION_CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>

        {/* Transactions Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{
            borderRadius: 2,
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)',
            overflowX: 'auto',
            width: '100%'
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(90deg, #004D40 0%, #00796B 50%, #26A69A 100%)'
                }}>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Reference</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Category</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Linked To</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Credit</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Debit</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Balance</TableCell>
                  <TableCell sx={{ color: 'white', whiteSpace: 'nowrap', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filterTransactions().map((transaction) => (
                  <TableRow key={transaction.id} hover>
                    <TableCell>{formatDate(transaction.date)}</TableCell>
                    <TableCell>{transaction.description}</TableCell>
                    <TableCell>{transaction.reference}</TableCell>
                    <TableCell>
                      <Chip label={transaction.category} size="small" sx={{
                        bgcolor: alpha(theme.palette.primary.light, 0.1),
                        color: theme.palette.primary.main
                      }} />
                    </TableCell>
                    <TableCell>
                      {transaction.linkedEntityName ? (
                        <Chip
                          label={transaction.linkedEntityName}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<UnlinkedIcon fontSize="small" />}
                          label="Unlinked"
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'success.main', fontWeight: transaction.type === 'credit' ? 'bold' : 'normal' }}>
                      {transaction.type === 'credit' ? formatCurrency(transaction.amount) : ''}
                    </TableCell>
                    <TableCell sx={{ color: 'error.main', fontWeight: transaction.type === 'debit' ? 'bold' : 'normal' }}>
                      {transaction.type === 'debit' ? formatCurrency(transaction.amount) : ''}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {formatCurrency(transaction.balance)}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditTransaction(transaction)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTransaction(transaction.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filterTransactions().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="subtitle1" color="text.secondary">
                        No transactions found matching your criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add Transaction Dialog */}
        <Dialog
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{editMode ? 'Edit Transaction' : 'Add New Cash Transaction'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Transaction Date"
                  value={new Date(newTransaction.date)}
                  onChange={(date) => setNewTransaction({
                    ...newTransaction,
                    date: date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
                  })}
                  slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Transaction Type</InputLabel>
                  <Select
                    name="type"
                    value={newTransaction.type}
                    onChange={(e) => {
                      handleTypeChange(e);
                      // Clear linked entities when changing transaction type
                      setSelectedClient(null);
                      setSelectedVendor(null);
                      setSelectedExpense(null);
                      setNewTransaction(prev => ({
                        ...prev,
                        linkedEntityType: '',
                        linkedEntityId: '',
                        linkedEntityName: ''
                      }));
                    }}
                    label="Transaction Type"
                  >
                    <MenuItem value="debit">Debit (Money Out)</MenuItem>
                    <MenuItem value="credit">Credit (Money In)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  label="Description"
                  margin="normal"
                  value={newTransaction.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="amount"
                  label="Amount"
                  type="number"
                  margin="normal"
                  value={newTransaction.amount || ''}
                  onChange={(e) => handleInputChange('amount', Number(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={newTransaction.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    label="Category"
                    required
                  >
                    {TRANSACTION_CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="reference"
                  label="Reference Number (Optional)"
                  margin="normal"
                  value={newTransaction.reference}
                  onChange={(e) => handleInputChange('reference', e.target.value)}
                />
              </Grid>

              {/* Linking section - new fields */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip label={`Link to ${newTransaction.type === 'credit' ? 'Client' : 'Vendor/Expense'}`} />
                </Divider>
              </Grid>

              {newTransaction.type === 'credit' ? (
                // Credit transactions should be linked to clients
                <Grid item xs={12}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option) => option.displayName || option.name || ''}
                    value={selectedClient}
                    onChange={(_, newValue) => handleLinkSelectionChange('client', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Link to Client"
                        fullWidth
                        required
                        helperText="Select the client this payment is from"
                      />
                    )}
                  />
                </Grid>
              ) : (
                // Debit transactions should be linked to vendors or expenses
                <>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={vendors}
                      getOptionLabel={(option) => option.displayName || option.name || ''}
                      value={selectedVendor}
                      onChange={(_, newValue) => {
                        handleLinkSelectionChange('vendor', newValue);
                        // Clear expense selection if a vendor is selected
                        if (newValue) {
                          setSelectedExpense(null);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Link to Vendor"
                          fullWidth
                          helperText="Select if this payment is to a vendor"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={expenses}
                      getOptionLabel={(option) => option.displayName || option.name || ''}
                      value={selectedExpense}
                      onChange={(_, newValue) => {
                        handleLinkSelectionChange('expense', newValue);
                        // Clear vendor selection if an expense is selected
                        if (newValue) {
                          setSelectedVendor(null);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Link to Expense"
                          fullWidth
                          helperText="Select if this payment is for an expense"
                        />
                      )}
                    />
                  </Grid>

                  {/* Option to create a new expense */}
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        startIcon={<NewEntryIcon />}
                        onClick={() => setCreateNewExpense(!createNewExpense)}
                        color="primary"
                        variant="outlined"
                        size="small"
                      >
                        {createNewExpense ? 'Cancel New Expense' : 'Create New Expense'}
                      </Button>
                    </Box>
                  </Grid>

                  {/* New expense form */}
                  {createNewExpense && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="name"
                          label="Expense Name"
                          margin="normal"
                          value={newExpense.name}
                          onChange={handleNewExpenseChange}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Expense Category</InputLabel>
                          <Select
                            name="category"
                            value={newExpense.category}
                            onChange={(e) => setNewExpense(prev => ({
                              ...prev,
                              category: e.target.value
                            }))}
                            label="Expense Category"
                            required
                          >
                            {TRANSACTION_CATEGORIES
                              .filter(category =>
                                category.includes('Expense') ||
                                category === 'Utilities' ||
                                category === 'Rent' ||
                                category === 'Insurance' ||
                                category === 'Office Supplies' ||
                                category === 'Travel' ||
                                category === 'Marketing'
                              )
                              .map((category) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          name="description"
                          label="Expense Description"
                          margin="normal"
                          value={newExpense.description}
                          onChange={handleNewExpenseChange}
                          multiline
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            onClick={handleCreateNewExpense}
                            variant="contained"
                            color="primary"
                          >
                            Save Expense & Link
                          </Button>
                        </Box>
                      </Grid>
                    </>
                  )}
                </>
              )}

              {/* Display the currently linked entity */}
              {newTransaction.linkedEntityId && (
                <Grid item xs={12}>
                  <Box sx={{
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    mt: 2
                  }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Linked to {newTransaction.linkedEntityType === 'client' ? 'Client' :
                        newTransaction.linkedEntityType === 'vendor' ? 'Vendor' : 'Expense'}:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" color="primary">
                      {newTransaction.linkedEntityName}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog}>Cancel</Button>
            <Button
              onClick={handleAddTransaction}
              variant="contained"
              color="primary"
              disabled={!isFormValid()}
            >
              {editMode ? 'Update' : 'Add'} Transaction
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this transaction? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={confirmDeleteTransaction} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Opening Balance Dialog */}
        <Dialog open={showOpeningBalanceDialog} onClose={handleCloseOpeningBalanceDialog}>
          <DialogTitle>Set Opening Balance</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Opening Balance"
                  type="number"
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(Number(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <DatePicker
                  label="Opening Balance Date"
                  value={openingBalanceDate}
                  onChange={(newValue) => setOpeningBalanceDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <DialogContentText>
                  This will set the opening balance for your cash book and will be used to calculate the running balance.
                </DialogContentText>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseOpeningBalanceDialog}>Cancel</Button>
            <Button onClick={handleSaveOpeningBalance} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Search Dialog */}
        <Dialog
          open={showSearchDialog}
          onClose={toggleSearchDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            elevation: 8,
            sx: {
              borderRadius: 2,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle>
            Search Transactions
          </DialogTitle>
          <CustomDialogContent>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              borderRadius: 2,
              px: 2,
              py: 0.5,
              position: 'relative'
            }}>
              <SearchIcon sx={{
                color: 'text.secondary',
                position: 'absolute',
                left: 12,
              }} />
              <SearchInput
                placeholder="Search by description, category, reference, amount..."
                value={searchTerm}
                onChange={handleSearchChange}
                autoFocus
              />
              {searchTerm && (
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                  sx={{ ml: 1 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Search results will be shown in the transactions table. Close this dialog to view full results.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tip: You can search by description, category, reference, amount, or linked entity.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
              <Button
                onClick={() => setSearchTerm('')}
                color="primary"
                startIcon={<ClearIcon />}
                disabled={!searchTerm}
              >
                Clear
              </Button>
              <Button onClick={toggleSearchDialog} color="primary">
                Close
              </Button>
            </Box>
          </CustomDialogContent>
        </Dialog>
      </Box>
    </AnimatedPage>
  );
};

export default CashBook; 
